import { Accordion, AccordionDetails, AccordionSummary, Link, Skeleton, TextField, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { ITimecard } from '../../interface/ISelectedDate'
import { setSelectedDate } from '../../source/slice/SelectedDateSlice'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WarningIcon from '@mui/icons-material/Warning'
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

const MAX_COMMENT_LENGTH = 255

const ViolationComment = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const selectedDate: ITimecard = useSelector((state: any) => state.selectedDate?.value)
    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)

    const formDisabled = selectedDate?.header?.isTransferred || selectedDate?.header?.calculated || selectedDate?.dayInformation?.mserp_transferred
    const [expanded, setExpanded] = useState<boolean>(false)
    const [charCount, setCharCount] = useState<number>(0)

    const changeViolationComment = useCallback((fieldName: string) => (event: any) => {
        const newselectedDate = JSON.parse(JSON.stringify(selectedDate))
        let newValue = event.target.value

        if (newValue && newValue.length > MAX_COMMENT_LENGTH) {
            newValue = newValue.substring(0, MAX_COMMENT_LENGTH)
        }

        setCharCount(newValue?.length || 0)

        if (newselectedDate !== undefined && newselectedDate.header !== undefined && newValue !== undefined) {
            newselectedDate.header[fieldName] = newValue
        }
        dispatch(setSelectedDate(newselectedDate))
    }, [selectedDate, dispatch])

    const handleBlur = useCallback((fieldName: string) => () => {
        const newselectedDate = JSON.parse(JSON.stringify(selectedDate))
        if (newselectedDate?.header?.[fieldName]) {
            newselectedDate.header[fieldName] = newselectedDate.header[fieldName].trim()
            dispatch(setSelectedDate(newselectedDate))
        }
    }, [selectedDate, dispatch])

    useEffect(() => {
        const comment = selectedDate?.header?.mserp_mbviolationcomment
        
        if (!expanded && comment && comment !== '' && comment !== null) {
            setExpanded(true)
        }
        setCharCount(comment?.length || 0)
    }, [selectedDate?.header?.mserp_mbviolationcomment, expanded])


    const violations = useMemo(() => [
        { key: 'violationBreakTime', label: t('zdeheader.violationBreakTime'), value: selectedDate?.header?.violationBreakTime },
        { key: 'violationDayAfter', label: t('zdeheader.violationDayAfter'), value: selectedDate?.header?.violationDayAfter },
        { key: 'violationDayBefore', label: t('zdeheader.violationDayBefore'), value: selectedDate?.header?.violationDayBefore },
        { key: 'violationMaximumTime', label: t('zdeheader.violationMaximumTime'), value: selectedDate?.header?.violationMaximumTime },
        { key: 'violationSundayWork', label: t('zdeheader.violationSundayWork'), value: selectedDate?.header?.violationSundayWork }
    ], [
        t,
        selectedDate?.header?.violationBreakTime,
        selectedDate?.header?.violationDayAfter,
        selectedDate?.header?.violationDayBefore,
        selectedDate?.header?.violationMaximumTime,
        selectedDate?.header?.violationSundayWork
    ])

    const getWarningIcon = () => {
        const hasViolation = violations.some(v => v.value === true)

        if (hasViolation) {
            const tooltipContent = (
                <div>
                    {violations.map(v => (
                        <div key={v.key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {v.value === true ? <DisabledByDefaultIcon fontSize="small" /> : <CheckBoxOutlineBlankIcon fontSize="small" />} {v.label}
                        </div>
                    ))}
                </div>
            )

            return !loadingDate ? (
                <Tooltip title={tooltipContent} arrow>
                    <WarningIcon color="warning" sx={{ width: { xs: '0.9em', sm: '1em' }, height: { xs: '0.9em', sm: '1em' } }} />
                </Tooltip>
            ) : null
        }
        return null
    }

    return (
        <Accordion sx={{ width: '100%', maxWidth: 550 }} expanded={expanded} onChange={() => setExpanded(!expanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('zdeheader.violationComment')} {getWarningIcon()}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Typography variant="body1" color="grey" fontSize={14}>
                    {t('zdeheader.violationIntro')}{' '}
                    <Link href="https://corpdir.sharepoint.com/sites/mbti-time-and-money/SitePages/FAQ-Arbeitszeit.aspx" target="_blank" rel="noopener noreferrer">
                        {t('zdeheader.violationLawLink')}
                    </Link>{' '}
                    {t('zdeheader.violationObligated')} <br />
                    <strong>{t('zdeheader.violationReason')}</strong>
                </Typography>
                {!loadingDate ? (
                    <>
                        <TextField
                            multiline
                            rows={5}
                            value={selectedDate?.header?.mserp_mbviolationcomment || ''}
                            onChange={changeViolationComment('mserp_mbviolationcomment')}
                            onBlur={handleBlur('mserp_mbviolationcomment')}
                            disabled={formDisabled}
                            sx={{
                                width: '100%',
                                '& .MuiInputBase-input::placeholder': {
                                    fontSize: '12px',
                                    opacity: 0.7,
                                },
                            }}
                            placeholder={!formDisabled ? t('zdeheader.violationPlaceholder') : ''}
                        />
                        {!formDisabled && (
                            <Typography variant="caption" color={charCount > MAX_COMMENT_LENGTH ? 'error' : 'textSecondary'} sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                                {charCount}/{MAX_COMMENT_LENGTH} {t('zdeheader.violationCharCount')}
                            </Typography>
                        )}
                    </>
                ) : (
                    <Skeleton variant="text" style={{ width: '100%', height: 64 }} />
                )}
            </AccordionDetails>
        </Accordion>
    )
}

export default ViolationComment
